import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getMyApplications } from '../services/applicationService';
import { Application } from '../types';
import { CalendarIcon, DocumentTextIcon, ArrowLeftIcon, BanknotesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const Payment: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }
    loadApplication();
  }, [user, applicationId]);

  const loadApplication = async () => {
    setLoading(true);
    try {
      const response = await getMyApplications('all');
      if (response.success && response.data) {
        const app = response.data.applications.find(
          (a) => a.application_id === applicationId
        );

        if (!app) {
          alert('신청 내역을 찾을 수 없습니다.');
          navigate('/mypage');
          return;
        }

        // 결제 가능 상태 검증
        if (app.status !== 'registered') {
          alert('접수 완료 상태가 아닙니다.');
          navigate('/mypage');
          return;
        }

        if (app.payment_status === 'completed') {
          alert('이미 결제가 완료된 신청입니다.');
          navigate('/mypage');
          return;
        }

        setApplication(app);
      } else {
        alert('신청 내역을 불러오는 중 오류가 발생했습니다.');
        navigate('/mypage');
      }
    } catch (error) {
      console.error('Error loading application:', error);
      alert('신청 내역을 불러오는 중 오류가 발생했습니다.');
      navigate('/mypage');
    } finally {
      setLoading(false);
    }
  };

  const isPaymentDeadlineExpired = (deadline: string | null | undefined) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center mt-20">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-500">로딩 중...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center mt-20">
          <div className="text-center py-12">
            <p className="text-gray-500">신청 내역을 찾을 수 없습니다.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <div className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 mt-4">
        {/* 뒤로가기 */}
        <button
          onClick={() => navigate('/mypage')}
          className="mb-6 inline-flex items-center text-sm font-semibold text-gray-700 hover:text-green-600 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          마이페이지로 돌아가기
        </button>

        {/* 무통장 입금 안내 카드 */}
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
          <div className="flex items-center mb-6">
            <BanknotesIcon className="h-8 w-8 text-green-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">무통장 입금 안내</h1>
          </div>

          {/* 프로그램 정보 */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">신청 정보</h2>
            <div className="space-y-3">
              <div className="flex items-start">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">프로그램명</p>
                  <p className="text-base font-medium text-gray-900">{application.program_name}</p>
                </div>
              </div>
              <div className="flex items-start">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">수강생</p>
                  <p className="text-base font-medium text-gray-900">{application.student_name}</p>
                </div>
              </div>
              <div className="flex items-start">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">신청 번호</p>
                  <p className="text-base font-medium text-gray-900">{application.application_number}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 결제 금액 정보 */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">결제 금액</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">수강료</span>
                <span className="text-gray-900">{application.tuition?.toLocaleString()}원</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">재료비</span>
                <span className="text-gray-900">{application.material_fee?.toLocaleString()}원</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">총 입금 금액</span>
                <span className="text-2xl font-bold text-green-600">
                  {application.payment_amount?.toLocaleString()}원
                </span>
              </div>
            </div>
          </div>

          {/* 입금 계좌 정보 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <BanknotesIcon className="h-5 w-5 mr-2" />
              입금 계좌 정보
            </h2>
            <div className="space-y-3 text-blue-800">
              <div className="flex items-center">
                <span className="font-medium w-20">은행명</span>
                <span className="text-lg font-semibold">농협은행</span>
              </div>
              <div className="flex items-center">
                <span className="font-medium w-20">계좌번호</span>
                <span className="text-lg font-semibold font-mono">301-0000-0000-00</span>
              </div>
              <div className="flex items-center">
                <span className="font-medium w-20">예금주</span>
                <span className="text-lg font-semibold">동두천시글로벌인재센터</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>입금자명:</strong> 반드시 <span className="font-bold">{application.student_name}</span>(으)로 입금해 주세요.
              </p>
            </div>
          </div>

          {/* 결제 기한 */}
          {application.payment_deadline && (
            <div className={`rounded-lg p-4 mb-6 ${
              isPaymentDeadlineExpired(application.payment_deadline)
                ? 'bg-red-50 border border-red-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-center">
                <CalendarIcon className={`h-5 w-5 mr-2 ${
                  isPaymentDeadlineExpired(application.payment_deadline)
                    ? 'text-red-600'
                    : 'text-yellow-600'
                }`} />
                <div>
                  <p className={`text-sm font-medium ${
                    isPaymentDeadlineExpired(application.payment_deadline)
                      ? 'text-red-800'
                      : 'text-yellow-800'
                  }`}>
                    입금 기한
                  </p>
                  <p className={`text-base font-semibold ${
                    isPaymentDeadlineExpired(application.payment_deadline)
                      ? 'text-red-900'
                      : 'text-yellow-900'
                  }`}>
                    {new Date(application.payment_deadline).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long'
                    })}
                    {isPaymentDeadlineExpired(application.payment_deadline) && (
                      <span className="ml-2 text-red-600">(기한 만료)</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 입금 확인 안내 */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800">입금 확인 안내</p>
                <p className="text-sm text-green-700 mt-1">
                  입금 확인은 영업일 기준 1~2일 소요되며, 확인 후 마이페이지에서 결제 완료 상태를 확인하실 수 있습니다.
                </p>
              </div>
            </div>
          </div>

          {/* 버튼 영역 */}
          <div className="space-y-3">
            <button
              onClick={() => navigate('/mypage')}
              className="w-full bg-green-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors"
            >
              마이페이지에서 확인하기
            </button>
          </div>
        </div>

        {/* 안내 사항 */}
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">유의 사항</h3>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>입금 기한 내에 입금하지 않으면 신청이 자동 취소될 수 있습니다.</li>
            <li>입금 시 반드시 신청자 이름으로 입금해 주세요.</li>
            <li>입금 확인 후 별도의 알림은 드리지 않습니다. 마이페이지에서 확인해 주세요.</li>
            <li>환불은 교육 시작 전까지 고객센터로 문의해 주세요.</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Payment;
